import { formatRelativeTime } from '@/utils';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import Button from 'antd/es/button';
import Modal from 'antd/es/modal';
import Table from 'antd/es/table';
import Tag from 'antd/es/tag';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import React, { useMemo } from 'react';
import { styled } from 'styled-components';

import type { GetPipelineExecutionsResponse } from '@mai/platform-api/src/routes/pipeline-executions.schema';

const { Text } = Typography;

interface ExecutionHistoryModalProps {
  open: boolean;
  onClose: () => void;
  executions: GetPipelineExecutionsResponse['items'] | undefined;
  isLoading?: boolean;
  onViewOutput: (execution: GetPipelineExecutionsResponse['items'][0]) => void;
  onRerunExecution?: (
    execution: GetPipelineExecutionsResponse['items'][0],
  ) => void;
  onViewDetails?: (
    execution: GetPipelineExecutionsResponse['items'][0],
  ) => void;
}

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 0;
  }
`;

const TableContainer = styled.div`
  max-height: 60vh;
  overflow-y: auto;
`;

const ActionButton = styled(Button)`
  margin-right: ${({ theme }) => theme.marginXS}px;

  &:last-child {
    margin-right: 0;
  }
`;

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    case 'failed':
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    case 'running':
      return <LoadingOutlined style={{ color: '#1890ff' }} />;
    case 'pending':
      return <ClockCircleOutlined style={{ color: '#faad14' }} />;
    default:
      return <ClockCircleOutlined />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'running':
      return 'processing';
    case 'pending':
      return 'warning';
    case 'cancelled':
      return 'default';
    default:
      return 'default';
  }
};

export const ExecutionHistoryModal: React.FC<ExecutionHistoryModalProps> = ({
  open,
  onClose,
  executions,
  isLoading,
  onViewOutput,
  onRerunExecution,
  onViewDetails,
}) => {
  const columns = useMemo(
    () => [
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status: string) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {getStatusIcon(status)}
            <Tag color={getStatusColor(status)}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Tag>
          </div>
        ),
      },
      {
        title: 'Input Summary',
        dataIndex: 'pipelineInputSummary',
        key: 'pipelineInputSummary',
        ellipsis: {
          showTitle: false,
        },
        render: (text: string) => (
          <Tooltip title={text}>
            <Text ellipsis>{text || 'Untitled Run'}</Text>
          </Tooltip>
        ),
      },
      {
        title: 'Started',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 120,
        render: (date: Date) => (
          <Text type="secondary">{formatRelativeTime(date)}</Text>
        ),
      },
      {
        title: 'Duration',
        key: 'duration',
        width: 100,
        render: (record: {
          startedAt?: Date;
          completedAt?: Date;
          failedAt?: Date;
          cancelledAt?: Date;
        }) => {
          if (!record.startedAt) return <Text type="secondary">—</Text>;
          const endTime =
            record.completedAt ||
            record.failedAt ||
            record.cancelledAt ||
            new Date();
          const duration =
            new Date(endTime).getTime() - new Date(record.startedAt).getTime();
          const minutes = Math.floor(duration / (1000 * 60));
          const seconds = Math.floor((duration % (1000 * 60)) / 1000);
          return (
            <Text type="secondary">
              {minutes > 0 ? `${minutes}m ` : ''}
              {seconds}s
            </Text>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 200,
        render: (record: GetPipelineExecutionsResponse['items'][0]) => (
          <div>
            <Tooltip title="View execution progress and output">
              <ActionButton
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => onViewOutput(record)}
              >
                View Output
              </ActionButton>
            </Tooltip>
            {onViewDetails && (
              <Tooltip title="View execution details">
                <ActionButton
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => onViewDetails(record)}
                >
                  Details
                </ActionButton>
              </Tooltip>
            )}
            {onRerunExecution && (
              <Tooltip title="Start new execution with same inputs">
                <ActionButton
                  type="link"
                  size="small"
                  icon={<PlayCircleOutlined />}
                  onClick={() => onRerunExecution(record)}
                >
                  Re-run
                </ActionButton>
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [onViewOutput, onRerunExecution, onViewDetails],
  );

  return (
    <StyledModal
      title="Execution History"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={900}
      destroyOnHidden
    >
      <TableContainer>
        <Table
          columns={columns}
          dataSource={executions || []}
          rowKey="pipelineExecutionId"
          loading={isLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} executions`,
          }}
          scroll={{ y: 400 }}
          size="small"
          onRow={(record) => ({
            onClick: () => onViewOutput(record),
            style: { cursor: 'pointer' },
          })}
        />
      </TableContainer>
    </StyledModal>
  );
};
